window.ttx = window.ttx || {};

// ============================================================
// MDF Biz Action 层（MDF 04- 章节）
// 把单据业务动作注册为 ttx.command 命令，并提供 ttx.biz.do / register / getActions API
//
// MDF biz.do 调用模式：
//   viewmodel.biz.do('actionName', viewmodel, params)
// 每个 action handler 签名（MDF 约定）：
//   actionName(billNo, viewmodel, params, beforeAct, afterAct) {
//     beforeAct(params, () => { /* 业务逻辑 */ afterAct({}); });
//   }
//   - beforeAct(params, callback)：前置钩子，callback 继续执行业务逻辑
//   - afterAct(result)：后置钩子，标识动作完成
//
// 同时每个 action 会注册为同名 ttx.command：
//   ttx.command.execute('save', { vm, params })  ↔  ttx.biz.do('save', vm, params)
//
// 设计说明：
//   - 注册 biz command 前先捕获既有 ttx.command，委托型 action 调用时回退到既有实现，
//     保留现有代码行为（不覆盖丢失 save/add/edit 等真实逻辑）。
//   - 委托型 action 的 before/after 事件由既有 command 自行触发，biz.do 不再重复触发，
//     避免同一监听器被两次回调。
// ============================================================
ttx.biz = ttx.biz || {};

// 注册表：按单据类型分组的 action 集合
ttx.biz._registry = {
    common: {},
    voucherlist: {},
    voucher: {},
    option: {},
    freeview: {}
};

// 注册 biz command 前捕获的既有 ttx.command（保留现有实现，避免覆盖丢失）
ttx.biz._existingCommands = {};

// 标记哪些 action 委托给既有 command
ttx.biz._delegatedActions = {};

// 命令注册是否已完成（防止重复注册）
ttx.biz._commandsRegistered = false;

// ============================================================
// 辅助方法
// ============================================================

/** 首字母大写 */
ttx.biz._capitalize = function (name) {
    if (!name) return '';
    var s = String(name);
    return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * 从 viewmodel 获取单据类型（voucherlist / voucher / option / freeview / common）
 * 优先读 vm 缓存的 billType，其次按模板 type 推导（1=列表 → voucherlist，2=详情 → voucher）
 */
ttx.biz._getBillType = function (viewmodel) {
    if (!viewmodel) return 'common';
    var cached = viewmodel.getCache && viewmodel.getCache('billType');
    if (cached) return cached;
    var tpl = viewmodel._get_data && viewmodel._get_data('template');
    var metaType = tpl && (tpl.type || (tpl.view && tpl.view.type));
    if (metaType === 1) return 'voucherlist';
    if (metaType === 2) return 'voucher';
    return 'common';
};

/** 从 viewmodel 获取单据号 */
ttx.biz._getBillNo = function (viewmodel) {
    if (!viewmodel) return '';
    var params = (viewmodel.getParams && viewmodel.getParams()) || {};
    return params.billno || params.billNo
        || (viewmodel._get_data && viewmodel._get_data('serviceCode'))
        || ((viewmodel._get_data && viewmodel._get_data('template')) || {}).serviceCode
        || '';
};

// ============================================================
// 核心 API
// ============================================================

/**
 * 注册某单据类型的 action 集合（合并到 _registry）
 * @param {string} billType  common / voucherlist / voucher / option / freeview
 * @param {object} actions   { actionName: handler }
 */
ttx.biz.register = function (billType, actions) {
    if (!billType || !actions) return;
    if (!ttx.biz._registry[billType]) ttx.biz._registry[billType] = {};
    Object.keys(actions).forEach(function (name) {
        ttx.biz._registry[billType][name] = actions[name];
    });
};

/**
 * 获取某单据类型的所有 action（合并 common 公共 + 专属）
 * voucher 卡片页额外继承 voucherlist 列表通用 action
 * @param {string} billType
 * @returns {object} { actionName: handler }
 */
ttx.biz.getActions = function (billType) {
    billType = billType || 'common';
    var actions = Object.assign({}, ttx.biz._registry.common || {});
    if (billType !== 'common') {
        // voucher 卡片页继承列表通用 action
        if (billType === 'voucher') {
            Object.assign(actions, ttx.biz._registry.voucherlist || {});
        }
        Object.assign(actions, ttx.biz._registry[billType] || {});
    }
    return actions;
};

/**
 * 执行业务动作
 * 流程：
 *   1. 触发 viewmodel.execute('before' + ActionName)，返回 false 中止
 *   2. 查找 handler（billType 专属 → common）
 *   3. 调用 handler(billNo, viewmodel, params, beforeAct, afterAct)
 *   4. 触发 viewmodel.execute('after' + ActionName)
 *
 * 注：委托型 action（已有同名 ttx.command）的 before/after 事件由既有 command 触发，
 *     此处跳过以避免重复回调。
 *
 * @param {string} actionName  动作名
 * @param {object} viewmodel   单据 ViewModel
 * @param {object} params      动作参数
 */
ttx.biz.do = function (actionName, viewmodel, params) {
    if (!actionName || !viewmodel) {
        ttx.console.warn('[BIZ] do 缺少参数：actionName=' + actionName + ' vm=' + !!viewmodel);
        return;
    }
    var billType = ttx.biz._getBillType(viewmodel);
    var billNo = ttx.biz._getBillNo(viewmodel);
    var capAction = ttx.biz._capitalize(actionName);
    var delegated = !!ttx.biz._delegatedActions[actionName];

    ttx.console.log('[BIZ] do: ' + actionName + ' billType=' + billType + ' billNo=' + billNo + ' delegated=' + delegated);

    // 1. 触发前置事件（委托型跳过，由既有 command 触发）
    if (!delegated && viewmodel.execute) {
        if (viewmodel.execute('before' + capAction, params) === false) {
            ttx.console.log('[BIZ] before' + capAction + ' 返回 false，中止 ' + actionName);
            return false;
        }
    }

    // 2. 查找 handler（billType 专属 → common）
    var handler = ttx.biz.getActions(billType)[actionName];
    if (typeof handler !== 'function') {
        ttx.console.warn('[BIZ] 未注册 action: ' + actionName + ' (billType=' + billType + ')');
        return;
    }

    // 构造 beforeAct / afterAct 钩子（act 级别事件，区别于 action 级别的 before/after）
    var beforeAct = function (p, callback) {
        ttx.console.log('[BIZ] beforeAct: ' + actionName);
        if (!delegated && viewmodel.execute) {
            viewmodel.execute('before' + capAction + 'Act', p);
        }
        if (typeof callback === 'function') callback();
    };
    var afterAct = function (result) {
        ttx.console.log('[BIZ] afterAct: ' + actionName, result);
        if (!delegated && viewmodel.execute) {
            viewmodel.execute('after' + capAction + 'Act', result);
        }
    };

    // 3. 调用 handler
    var ret = handler(billNo, viewmodel, params, beforeAct, afterAct);

    // 4. 触发后置事件（委托型跳过）
    if (!delegated && viewmodel.execute) {
        viewmodel.execute('after' + capAction, ret);
    }

    return ret;
};

// ============================================================
// handler 工厂
// ============================================================

// 当前 command 调用上下文（由 command 包装器设置，供委托 handler 还原完整 ctx，
// 避免 jointQueryParams / button 等字段在委托时丢失）
var _currentCtx = null;

/**
 * 占位 handler 工厂：beforeAct → (TODO 待对接后端) → afterAct
 * @param {string} actionName 动作名（用于日志）
 */
function _placeholder(actionName) {
    return function (billNo, viewmodel, params, beforeAct, afterAct) {
        beforeAct(params, function () {
            // TODO: 待对接后端 —— <actionName>
            ttx.console.log('[BIZ] ' + actionName + ' 占位执行, billNo=' + billNo);
            afterAct({});
        });
    };
}

/**
 * 委托同名既有 ttx.command 的 handler 工厂（保留现有实现）
 * @param {string} cmdName 既有 command 名
 */
function _delegateTo(cmdName) {
    return function (billNo, viewmodel, params, beforeAct, afterAct) {
        var result;
        beforeAct(params, function () {
            var existing = ttx.biz._existingCommands[cmdName];
            var ctx = _currentCtx || { vm: viewmodel, params: params };
            console.log('[addRow][BIZ委托] ' + cmdName + ' existing=' + (typeof existing) + ' ctx.containerKey=' + (ctx?.containerKey || 'undefined'));
            if (typeof existing === 'function') {
                result = existing(ctx);
            } else {
                ttx.console.warn('[BIZ] ' + cmdName + ' 无既有 command 可委托');
                console.warn('[addRow][BIZ委托] ' + cmdName + ' 无既有command, _existingCommandskeys=' + Object.keys(ttx.biz._existingCommands || {}).join(','));
            }
            afterAct({});
        });
        return result;
    };
}

/**
 * 委托任意既有 command（非同名）的 handler 工厂
 * 用于语义等价但名称不同的动作（如 output → export、goback → back）
 * @param {string} cmdName 既有 command 名
 */
function _delegateToOther(cmdName) {
    return function (billNo, viewmodel, params, beforeAct, afterAct) {
        var result;
        beforeAct(params, function () {
            var cmd = ttx.command && ttx.command.commands && ttx.command.commands[cmdName];
            var ctx = _currentCtx || { vm: viewmodel, params: params };
            if (typeof cmd === 'function') {
                result = cmd(ctx);
            } else {
                ttx.console.warn('[BIZ] ' + cmdName + ' command 不存在，占位执行');
            }
            afterAct({});
        });
        return result;
    };
}

// ============================================================
// common 公共 action（所有单据通用，8 个，真实逻辑）
// 逻辑签名见各 handler 注释；统一适配为 MDF 5 参签名，真实参数从 params 取
// ============================================================
ttx.biz.register('common', {

    /**
     * 单据拷贝（逻辑签名：billCopy(source, target)）
     * 浅拷贝 source 字段到 target
     */
    billCopy(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            var source = p.source;
            var target = p.target || {};
            if (source && typeof source === 'object') {
                Object.assign(target, source);
            }
            afterAct({ target: target });
        });
    },

    /**
     * 单据合并（逻辑签名：billMerge(source, target, condition, processArray)）
     * 遍历 source，满足 condition 的项合并入 target
     */
    billMerge(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            var source = p.source;
            var target = p.target || (Array.isArray(source) ? [] : {});
            var condition = p.condition;
            var processArray = p.processArray;
            if (Array.isArray(source) && Array.isArray(target)) {
                // 数组合并：遍历 source，满足条件者推入 target
                source.forEach(function (item) {
                    if (typeof condition !== 'function' || condition(item)) {
                        target.push(processArray ? processArray(item) : item);
                    }
                });
            } else if (source && typeof source === 'object' && typeof target === 'object') {
                // 对象合并：遍历 source 自有属性
                for (var k in source) {
                    if (source.hasOwnProperty(k) && (typeof condition !== 'function' || condition(k, source[k]))) {
                        target[k] = source[k];
                    }
                }
            }
            afterAct({ target: target });
        });
    },

    /**
     * 设置 VM 参数（逻辑签名：setVmParams(viewmodel, key, value)）
     * 同时写入 cache 与 data，便于不同读取路径获取
     */
    setVmParams(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            var key = p.key;
            var value = p.value;
            if (viewmodel) {
                if (typeof viewmodel.setCache === 'function') viewmodel.setCache(key, value);
                if (typeof viewmodel._set_data === 'function') viewmodel._set_data(key, value);
            }
            afterAct({ key: key, value: value });
        });
    },

    /**
     * 获取 VM 参数（逻辑签名：getVmParams(viewmodel, key)）
     * beforeAct 同步执行 callback，故 handler 同步返回 value
     */
    getVmParams(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        var value;
        beforeAct(p, function () {
            var key = p.key;
            if (viewmodel) {
                if (typeof viewmodel.getCache === 'function') value = viewmodel.getCache(key);
                if (value === undefined && typeof viewmodel._get_data === 'function') value = viewmodel._get_data(key);
            }
            afterAct({ value: value });
        });
        return value;
    },

    /**
     * 执行请求（逻辑签名：doProxy(params, callback, data)）
     * 调用 ttx.ajax.xhrUtil 发起请求，成功/失败分别回调
     */
    doProxy(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            ttx.ajax.xhrUtil({
                url: p.url,
                method: p.method || 'post',
                data: p.data || p.requestData,
                success: function (res) {
                    if (typeof p.callback === 'function') p.callback(res);
                    afterAct({ res: res, success: true });
                },
                fail: function (err) {
                    afterAct({ err: err, success: false });
                }
            });
        });
    },

    /**
     * 拉取 UI 元数据（逻辑签名：fetchMeta(billNo, params, callback)）
     * 调用 ttx.loader.createUIDOM
     */
    fetchMeta(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            var query = { id: p.metaid || p.id || p.tplId };
            if (billNo) query.billno = billNo;
            ttx.loader.createUIDOM(query, function (meta) {
                if (typeof p.callback === 'function') p.callback(meta);
                afterAct({ meta: meta });
            });
        });
    },

    /**
     * 执行（逻辑签名：todo(self, act, viewmodel, params)）
     * 在 self（默认 viewmodel）上执行指定动作
     */
    todo(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            var act = p.act;
            var target = p.self || viewmodel;
            if (target && typeof target.execute === 'function' && act) {
                target.execute(act, p);
            }
            afterAct({});
        });
    },

    /**
     * 批量校验（逻辑签名：batchCheck(billNo, viewModel, params)）
     * 触发 beforeCheck 事件，返回 false 中止校验
     */
    batchCheck(billNo, viewmodel, params, beforeAct, afterAct) {
        var p = params || {};
        beforeAct(p, function () {
            if (viewmodel && typeof viewmodel.execute === 'function') {
                if (viewmodel.execute('beforeCheck', p) === false) {
                    afterAct({ passed: false, aborted: true });
                    return;
                }
            }
            // TODO: 批量校验具体规则待对接后端
            afterAct({ passed: true });
        });
    }
});

// ============================================================
// voucherlist 列表页 action（31 个）
// 同名既有 command（add/edit/save/pull/refresh/print/delete/submit/jointquery）委托既有实现，
// 其余为占位（beforeAct/afterAct 框架就绪，业务逻辑待对接后端）
// ============================================================
ttx.biz.register('voucherlist', {
    add: _delegateTo('add'),
    edit: _delegateTo('edit'),
    save: _delegateTo('save'),
    abandon: _placeholder('abandon'),
    copy: _placeholder('copy'),
    push: _placeholder('push'),
    pull: _delegateTo('pull'),
    refresh: _delegateTo('refresh'),
    output: _delegateToOther('export'),        // 导出 ≈ export
    print: _delegateTo('print'),
    printpreview: _placeholder('printpreview'),
    audit: _placeholder('audit'),
    batchaudit: _placeholder('batchaudit'),
    submit: _delegateTo('submit'),
    batchsubmit: _placeholder('batchsubmit'),
    unaudit: _placeholder('unaudit'),
    batchunaudit: _placeholder('batchunaudit'),
    unsubmit: _placeholder('unsubmit'),
    batchunsubmit: _placeholder('batchunsubmit'),
    close: _placeholder('close'),
    open: _placeholder('open'),
    delete: _delegateTo('delete'),
    batchdelete: _placeholder('batchdelete'),
    workflow: _placeholder('workflow'),
    // TODO: 大小写待统一 —— biz 注册小写 'jointquery'，但 SDK 原始命令 key 是驼峰 'jointQuery'（core 工程调用驼峰）。
    // 当前 _existingCommands['jointquery'] 为 undefined，biz.do('jointquery') 会 warn。
    // 不影响 core（core 走 ttx.command.execute('jointQuery') 原始命令），但后续应统一为 'jointQuery' 以匹配原始 key。
    jointquery: _delegateTo('jointquery'),
    batchmodify: _placeholder('batchmodify'),
    columnsetting: _placeholder('columnsetting'),
    goback: _delegateToOther('back'),          // 返回 ≈ back
    attachment: _placeholder('attachment'),
    check: _placeholder('check'),
    autocomplete: _placeholder('autocomplete')
});

// ============================================================
// voucher 卡片页特有 action（注册到 'voucher' 类型）
// voucher 页通过 getActions('voucher') 继承 common + voucherlist + voucher
// 同名既有 command（addrow/deleterow 来自 table scope）委托既有实现，其余占位
// ============================================================
ttx.biz.register('voucher', {
    insertrow: _placeholder('insertrow'),
    deleterow: _delegateTo('deleterow'),
    copyrow: _placeholder('copyrow'),
    shiftuprow: _placeholder('shiftuprow'),
    shiftdownrow: _placeholder('shiftdownrow'),
    addrow: _delegateTo('addrow'),             // 后置事件推荐 afterInsertRow
    processWorkflow: _placeholder('processWorkflow'),
    search: _placeholder('search'),
    code: _placeholder('code'),
    org: _placeholder('org'),
    transtype: _placeholder('transtype'),
    rule: _placeholder('rule'),
    movefirst: _placeholder('movefirst'),
    movelast: _placeholder('movelast'),
    moveprev: _placeholder('moveprev'),
    movenext: _placeholder('movenext'),
    create: _placeholder('create'),
    saveandadd: _placeholder('saveandadd'),
    saveandcopy: _placeholder('saveandcopy'),
    saveandaudit: _placeholder('saveandaudit'),
    savedraft: _placeholder('savedraft'),
    voucherattachment: _placeholder('voucherattachment'),
    rowdetail: _placeholder('rowdetail'),
    formularule: _placeholder('formularule'),
    bizflowpush: _placeholder('bizflowpush'),
    querydetailtable: _placeholder('querydetailtable'),
    changebilldiff: _placeholder('changebilldiff'),
    unchangebilldiff: _placeholder('unchangebilldiff'),
    changehistory: _placeholder('changehistory'),
    moveup: _placeholder('moveup'),
    movedown: _placeholder('movedown'),
    moveto: _placeholder('moveto'),
    totop: _placeholder('totop'),
    tobottom: _placeholder('tobottom')
});

// ============================================================
// option 参数选项 action（3 个）
// ============================================================
ttx.biz.register('option', {
    load: _placeholder('load'),
    save: _delegateTo('save'),
    abandon: _placeholder('abandon')
});

// ============================================================
// freeview 自由单据 action（继承 common，特有 cellcheck）
// ============================================================
ttx.biz.register('freeview', {
    cellcheck: _placeholder('cellcheck')
});

// ============================================================
// 把每个 biz action 注册为同名 ttx.command
//   ttx.command.execute('save', { vm, params })  ↔  ttx.biz.do('save', vm, params)
// 注册前先捕获既有 command，委托型 action 调用时回退到既有实现，保留现有代码行为
// ============================================================

/** 构造 ttx.command 包装器：把 {vm, params} 上下文适配为 biz.do 调用 */
function _makeCommandWrapper(actionName) {
    return function (ctx) {
        ctx = ctx || {};
        _currentCtx = ctx;
        try {
            return ttx.biz.do(actionName, ctx.vm, ctx.params);
        } finally {
            _currentCtx = null;
        }
    };
}

/**
 * 把所有 biz action 注册为同名 ttx.command
 * 延迟到 ttx.command 及既有命令就绪后执行（模块加载完成后通过 setTimeout 触发）
 */
ttx.biz._registerCommands = function () {
    if (!ttx.command || typeof ttx.command.register !== 'function' || !ttx.command.commands) {
        // ttx.command 尚未就绪，延迟重试
        setTimeout(ttx.biz._registerCommands, 0);
        return;
    }
    if (ttx.biz._commandsRegistered) return;
    ttx.biz._commandsRegistered = true;

    // 收集所有 action 名（并集）
    var allNames = {};
    Object.keys(ttx.biz._registry).forEach(function (billType) {
        Object.keys(ttx.biz._registry[billType]).forEach(function (name) {
            allNames[name] = true;
        });
    });

    var captured = 0, registered = 0;
    Object.keys(allNames).forEach(function (name) {
        // 捕获既有 command（保留现有实现，按 normalized key 查找）
        var key = String(name).toLowerCase();
        if (ttx.command.commands[key] && !ttx.biz._existingCommands[name]) {
            ttx.biz._existingCommands[name] = ttx.command.commands[key];
            ttx.biz._delegatedActions[name] = true;
            captured++;
        }
        // 注册 biz 包装命令（覆盖同名 command，内部通过 biz.do 调度）
        ttx.command.register(name, _makeCommandWrapper(name));
        registered++;
    });

    ttx.console.log('[BIZ] 已注册 ' + registered + ' 个 biz command，其中 ' + captured + ' 个委托既有实现');
};

// 模块加载后延迟注册（确保 ttx.command 及 card/table scope 已就绪）
setTimeout(ttx.biz._registerCommands, 0);
